import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Pie, PieChart } from "recharts";
import colors from "tailwindcss/colors";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierActiveLineStatsQuery } from "@/app/Suppliers/GraphQL/supplierActiveLineStatsQuery";

export function SupplierActiveLinesPieChart({
  supplierId,
}: {
  supplierId: string;
}) {
  const { data } = useSuspenseGqlQuery(supplierActiveLineStatsQuery, {
    supplierId,
  });

  const stats = data.supplierActiveLineStats;

  const statusMetrics = [
    {
      key: "available",
      label: "Available",
      count: stats.available_count,
      fill: colors.green[500],
    },
    {
      key: "imoova",
      label: "Imoova",
      count: stats.imoova_count,
      fill: "#F97316", // Imoova orange
    },
    {
      key: "transfercar",
      label: "TransferCar",
      count: stats.transfercar_count,
      fill: "#0891B2", // TransferCar teal
    },
    {
      key: "coseats",
      label: "CoSeats",
      count: stats.coseats_count,
      fill: "#7C3AED", // CoSeats purple
    },
    {
      key: "drivenow",
      label: "DriveNow",
      count: stats.drivenow_count,
      fill: "#DC2626", // DriveNow red
    },
  ];

  const chartData = statusMetrics
    .filter((item) => item.count > 0)
    .map((item) => ({
      status: item.label,
      count: item.count,
      fill: item.fill,
    }));

  const chartConfig = {
    count: {
      label: "Plates",
    },
  } satisfies ChartConfig;

  const totalLines = statusMetrics.reduce(
    (sum, metric) => sum + metric.count,
    0,
  );

  if (totalLines === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Plate Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          <ChartContainer config={chartConfig} className="h-48 flex-1">
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent />} />
              <Pie data={chartData} dataKey="count" nameKey="status" />
            </PieChart>
          </ChartContainer>

          <div className="flex flex-col justify-center gap-2">
            {statusMetrics
              .filter((item) => item.count > 0)
              .map((item) => (
                <div key={item.key} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: item.fill }}
                  />
                  <div className="text-sm">
                    <span className="font-medium">{item.label}</span>
                    <span className="ml-2 text-gray-500">
                      {item.count} (
                      {totalLines > 0
                        ? Math.round((item.count / totalLines) * 100)
                        : 0}
                      %)
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
